import React, { useMemo, useState } from "react";
import MyCalendar from "../../components/Home/MyCalendar";
import { Dialog, DialogTitle } from "@mui/material";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";

import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import NewspaperRoundedIcon from "@mui/icons-material/NewspaperRounded";

import FeaturedTool from "../../components/Home/FeaturedTool";
import HomeWeatherWidget from "../../components/Home/HomeWeatherWidget";
import HomeNotifications from "../../components/Home/HomeNotifications";
import HomeWidgets from "../../components/Home/HomeWidgets";
import QuickAccess from "../../components/Home/QuickAccess";
import { news } from "../../components/Home/newsData";
import { PageShell, SectionHeader } from "../../components/UI/Page";

const parseNewsDate = (iso) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};

const formatNewsDate = (iso) => {
  const date = parseNewsDate(iso);
  if (!date) return iso;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// News from the last two weeks gets a small "New" tag.
const isRecent = (iso) => {
  const date = parseNewsDate(iso);
  return date ? Date.now() - date.getTime() < 14 * 86400000 : false;
};

const Home = () => {
  const [openCalendar, setOpenCalendar] = useState(false);
  const handleClose = () => setOpenCalendar(false);
  const user = useSelector(selectUser);
  const firstName = user?.name?.split(" ")[0];

  // Newest first; entries without a date sort oldest. Show the three most
  // recent — see components/Home/newsData.js for the maintenance rule.
  const latestNews = useMemo(
    () =>
      [...news]
        .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
        .slice(0, 3),
    []
  );

  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="app-canvas min-h-[calc(100vh-140px)] w-full">
      <PageShell size="xl" className="space-y-10">
        {/* Hero — a translucent card with a soft sky/violet wash. The wash
            and glow live in their own clipped layer so the card needs no
            overflow/filter of its own. */}
        <section className="app-card relative isolate rounded-[1.75rem] border px-6 py-7 sm:px-8 sm:py-9 lg:px-10">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-[inherit]"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-sky-400/20 via-violet-400/[0.12] to-transparent dark:from-sky-500/[0.18] dark:via-violet-500/[0.12]" />
            <div className="absolute -right-24 -top-32 h-80 w-80 rounded-full bg-violet-400/30 blur-3xl dark:bg-violet-500/25" />
            <div className="absolute -bottom-36 -left-20 h-72 w-72 rounded-full bg-sky-400/25 blur-3xl dark:bg-sky-500/20" />
            <div className="absolute -bottom-24 right-1/4 h-48 w-48 rounded-full bg-amber-300/20 blur-3xl dark:bg-teal-400/10" />
          </div>

          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="min-w-0">
              <p className="app-eyebrow flex flex-wrap items-center gap-x-2">
                <span>Big C Lumber Portal</span>
                <span aria-hidden="true" className="opacity-50">
                  •
                </span>
                <span>{today}</span>
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-[color:var(--app-text)] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
                Welcome back
                {firstName && (
                  <>
                    ,{" "}
                    <span className="bg-gradient-to-r from-sky-600 to-violet-600 bg-clip-text text-transparent dark:from-sky-300 dark:to-violet-300">
                      {firstName}
                    </span>
                  </>
                )}
              </h1>
              <p className="app-muted mt-3 max-w-xl text-[0.95rem] leading-relaxed">
                Scheduling, quoting, and everything you need — all in one
                place.
              </p>
              {user?.branch && (
                <span className="app-chip mt-4 py-1 pl-2 pr-3 text-[0.8rem]">
                  <PlaceRoundedIcon
                    sx={{ fontSize: 15 }}
                    className="text-sky-600 dark:text-sky-300"
                  />
                  {user.branch}
                </span>
              )}
            </div>
            <div className="shrink-0">
              <HomeWeatherWidget />
            </div>
          </div>
        </section>

        {/* Unread notifications (renders nothing when empty) */}
        <HomeNotifications />

        {/* Quick access: three defaults for everyone + the user's favorites.
            Finding a page by name lives in the header (Search pages… / ⌘K). */}
        <QuickAccess onOpenCalendar={() => setOpenCalendar(true)} />

        {/* Personal widgets */}
        <HomeWidgets />

        {/* Featured tool — slim daily-rotating spotlight */}
        <FeaturedTool />

        {/* Latest news */}
        <section aria-labelledby="home-news-title">
          <SectionHeader title={<span id="home-news-title">Latest news</span>} />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {latestNews.map((item) => (
              <article
                key={item.id}
                className="app-card flex flex-col rounded-2xl border p-5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="app-icon-tile h-9 w-9 rounded-xl bg-violet-500/15 text-violet-600 dark:text-violet-300">
                    <NewspaperRoundedIcon sx={{ fontSize: 18 }} />
                  </span>
                  <span className="flex items-center gap-2">
                    {isRecent(item.date) && (
                      <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                        New
                      </span>
                    )}
                    {item.date && (
                      <time
                        dateTime={item.date}
                        className="app-muted text-xs tabular-nums"
                      >
                        {formatNewsDate(item.date)}
                      </time>
                    )}
                  </span>
                </div>
                <h3 className="mt-3 font-semibold leading-snug tracking-tight">
                  {item.title}
                </h3>
                <p className="app-muted mt-1.5 text-sm leading-relaxed">
                  {item.body}
                </p>
                {item.link && (
                  <Link
                    to={item.link.to}
                    className="app-link mt-auto inline-flex items-center gap-1 self-start pt-3 text-sm"
                  >
                    {item.link.label}
                    <ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />
                  </Link>
                )}
              </article>
            ))}
          </div>
        </section>
      </PageShell>

      {/* Calendar Dialog */}
      <Dialog
        maxWidth="xl"
        fullWidth
        open={openCalendar}
        onClose={handleClose}
        aria-labelledby="home-calendar-title"
      >
        <DialogTitle
          id="home-calendar-title"
          className="flex items-center justify-between gap-3"
        >
          Calendar
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close calendar"
            className="app-icon-btn -mr-2 h-9 w-9"
          >
            <CloseRoundedIcon fontSize="small" />
          </button>
        </DialogTitle>
        <MyCalendar />
      </Dialog>
    </div>
  );
};

export default Home;
